import {useCallback, useState} from 'react';
import {ScrollView, View} from 'react-native';
import {useFocusEffect} from '@react-navigation/native';
import {Button, TextInput} from 'react-native-paper';
import {Text} from '../../Components';
import {PageContainer} from '../../Containers';
import {useTheme} from '../../Hooks';
import {RootDataSharingScreenProps} from '../../Typings/NavigationTypes';
import {FEEDBACK_EMAIL, openFeedbackEmail} from '../../Utils/Contact';
import {getCopy} from '../../Utils/I18n';
import {
  DeletionResult,
  TelemetryConfig,
  TelemetryError,
  deleteSharedData,
  disableSharing,
  enableSharing,
  flushTelemetry,
  getPendingCount,
  loadTelemetryConfig,
  parseSetupCode,
} from '../../Utils/Telemetry';
import getStyles from './ADAPT-MH.styles';

type Props = RootDataSharingScreenProps;
type PendingConfirmation = 'disable' | 'delete' | null;

const fill = (template: string, values: Record<string, string | number>): string =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.replace(`{${key}}`, String(value)),
    template,
  );

const DataSharing: React.FC<Props> = ({route}) => {
  const theme = useTheme();
  const styles = getStyles(theme);
  const copy = getCopy(route.params?.language || 'en').dataSharing;

  const [config, setConfig] = useState<TelemetryConfig | null>(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [setupCode, setSetupCode] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<PendingConfirmation>(null);

  const reload = useCallback(async () => {
    setConfig(await loadTelemetryConfig());
    setPendingCount(await getPendingCount());
  }, []);

  useFocusEffect(
    useCallback(() => {
      reload();
    }, [reload]),
  );

  const run = async (action: () => Promise<void>, failureMessage: string) => {
    setIsBusy(true);
    setErrorMessage(null);
    setInfoMessage(null);
    try {
      await action();
    } catch (error) {
      const status = error instanceof TelemetryError ? error.status : undefined;
      setErrorMessage(status === 400 || status === 401 ? copy.invalidCode : failureMessage);
    } finally {
      setIsBusy(false);
      setConfirmation(null);
      await reload();
    }
  };

  const onEnablePress = () => {
    if (!parseSetupCode(setupCode)) {
      setErrorMessage(copy.invalidCode);
      return;
    }
    run(async () => {
      await enableSharing(setupCode);
      setSetupCode('');
    }, copy.verifyFailed);
  };

  const onSendNowPress = () => run(() => flushTelemetry(), copy.verifyFailed);

  const onDisableConfirmed = () => run(() => disableSharing(), copy.verifyFailed);

  const onDeleteConfirmed = () =>
    run(async () => {
      const deleted: DeletionResult = await deleteSharedData();
      setInfoMessage(fill(copy.deleteDone, deleted));
    }, copy.deleteFailed);

  const isEnabled = Boolean(config?.enabled);

  const renderBullets = (title: string, bullets: string[]) => (
    <View style={styles.card}>
      <Text weight="bold" fontSize={17} style={styles.strongBody}>
        {title}
      </Text>
      {bullets.map((bullet) => (
        <Text key={bullet} style={styles.listItem}>
          {`- ${bullet}`}
        </Text>
      ))}
    </View>
  );

  const renderConfirmation = () => {
    if (!confirmation) return null;
    const isDelete = confirmation === 'delete';
    return (
      <View style={[styles.card, isDelete ? styles.urgentCard : null]}>
        <Text style={styles.strongBody}>{isDelete ? copy.deleteConfirm : copy.disableConfirm}</Text>
        <View style={styles.row}>
          <Button
            mode="text"
            onPress={() => setConfirmation(null)}
            disabled={isBusy}
            textColor={theme.colors.textLight}
          >
            {copy.cancel}
          </Button>
          <Button
            mode="contained"
            onPress={isDelete ? onDeleteConfirmed : onDisableConfirmed}
            disabled={isBusy}
            loading={isBusy}
            buttonColor={isDelete ? theme.colors.error : theme.colors.primary}
            textColor={theme.colors.textContrast}
          >
            {copy.confirm}
          </Button>
        </View>
      </View>
    );
  };

  return (
    <PageContainer withSafeArea withPadding>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <View style={styles.hero}>
          <Text
            fontSize={30}
            lineHeight={37}
            weight="black"
            accessibilityRole="header"
            style={styles.title}
          >
            {copy.title}
          </Text>
          <Text fontSize={16} lineHeight={24} style={styles.subtitle}>
            {copy.subtitle}
          </Text>
        </View>

        <View style={styles.softCard}>
          <Text weight="bold" fontSize={17} style={styles.strongBody}>
            {isEnabled && config
              ? fill(copy.statusOn, {
                  site: config.siteCode,
                  date: new Date(config.consentedAt).toLocaleDateString(),
                })
              : copy.statusOff}
          </Text>
          {isEnabled ? (
            <>
              <Text style={styles.listItem}>{fill(copy.pending, {count: pendingCount})}</Text>
              {pendingCount > 0 ? (
                <Button
                  mode="outlined"
                  onPress={onSendNowPress}
                  disabled={isBusy}
                  style={styles.secondaryButton}
                  textColor={theme.colors.primary}
                >
                  {copy.sendNow}
                </Button>
              ) : null}
            </>
          ) : null}
        </View>

        {errorMessage ? (
          <Text style={styles.warningText} accessibilityLiveRegion="polite">
            {errorMessage}
          </Text>
        ) : null}
        {infoMessage ? (
          <Text style={styles.strongBody} accessibilityLiveRegion="polite">
            {infoMessage}
          </Text>
        ) : null}

        {renderBullets(copy.consentTitle, copy.consentBullets)}
        {renderBullets(copy.notSharedTitle, copy.notSharedBullets)}

        <View style={styles.softCard}>
          <Text style={styles.body}>{copy.mockNote}</Text>
          <Text style={styles.listItem}>{copy.storageNote}</Text>
          <Text style={styles.listItem}>{copy.contact}</Text>
          <Text
            accessibilityRole="link"
            accessibilityLabel={`Email ${FEEDBACK_EMAIL}`}
            onPress={() => openFeedbackEmail('ADAPT-MH research data')}
            style={[styles.listItem, styles.link]}
          >
            {FEEDBACK_EMAIL}
          </Text>
        </View>

        {isEnabled ? (
          <>
            {renderConfirmation()}
            {!confirmation ? (
              <View style={styles.buttonGroup}>
                <Button
                  mode="outlined"
                  onPress={() => setConfirmation('disable')}
                  disabled={isBusy}
                  style={styles.secondaryButton}
                  textColor={theme.colors.primary}
                >
                  {copy.disableButton}
                </Button>
                <Button
                  mode="outlined"
                  onPress={() => setConfirmation('delete')}
                  disabled={isBusy}
                  style={styles.secondaryButton}
                  textColor={theme.colors.error}
                >
                  {copy.deleteButton}
                </Button>
              </View>
            ) : null}
          </>
        ) : (
          <View style={styles.card}>
            <Text weight="bold" fontSize={17} style={styles.strongBody}>
              {copy.setupTitle}
            </Text>
            <Text style={styles.listItem}>{copy.setupHint}</Text>
            <TextInput
              mode="outlined"
              label={copy.setupCodeLabel}
              value={setupCode}
              onChangeText={setSetupCode}
              autoCapitalize="none"
              autoCorrect={false}
              autoComplete="off"
              style={styles.input}
            />
            <Button
              mode="contained"
              onPress={onEnablePress}
              disabled={isBusy || setupCode.trim().length === 0}
              loading={isBusy}
              style={styles.button}
              buttonColor={theme.colors.primary}
              textColor={theme.colors.textContrast}
            >
              {isBusy ? copy.verifying : copy.consentButton}
            </Button>
            {config && !config.enabled ? (
              <Button
                mode="text"
                onPress={() => setConfirmation('delete')}
                disabled={isBusy}
                style={styles.secondaryButton}
                textColor={theme.colors.error}
              >
                {copy.deleteButton}
              </Button>
            ) : null}
            {renderConfirmation()}
          </View>
        )}
      </ScrollView>
    </PageContainer>
  );
};

export default DataSharing;
